/**
 * Which row-expanding "fan" a Search Results column rides, and the rule that keeps a
 * grid to one of them.
 *
 * Two columns whose branches differ join the pro one-to-many twice, so the grid shows
 * N x M rows — Main-invoice lines x Any-invoice lines. One branch at a time keeps one
 * grid row meaning exactly one child record.
 *
 * The branch itself comes from the SERVER, on each field definition
 * (`expanding_branch`, walked from join_definitions.depends_on_join_key), the same way
 * `expands` does. It used to be a hardcoded map in SearchResults.tsx and it drifted
 * from the join graph: the additional-cost joins were never added, so the grid refused
 * `Additional Cost - Type` next to `Additional Cost Line - Group Responsible` even
 * though both ride the one `ac` fan and the backend served them fine
 * (Lisa, 2026-10-25). A new join now needs no entry here at all.
 */
import type { FieldDefinition } from '../../contexts/FieldDefinitionsContext';

/** Only for a registry cached before the server sent `expanding_branch`. New joins
 *  must NOT be added here — the server derives them. */
export const EXPANDING_BRANCH_FALLBACK: Record<string, string> = {
  invoice: 'invoice',
  invoice_lineitem: 'invoice',
  invoice_main_lineitem: 'invoice_main_lineitem',
  troubleticket: 'troubleticket',
  hazpro: 'hazpro',
  addlcost: 'addlcost',
  addlcost_lineitem: 'addlcost',
  addlcost_vendor: 'addlcost',
  driverpay_lineitem: 'driverpay_lineitem',
  driverpay_lineitempro: 'driverpay_lineitem',
  // The generic leg join and its two location children ride the same `leg` row, so
  // showing all three fans the pro once, not three times.
  leg: 'leg',
  leg_dest: 'leg',
  leg_origin: 'leg',
};

/** The fan a field rides, or null when displaying it cannot duplicate the row.
 *  `fieldExpands` is the caller's — a surface that fans legs ORs in `expands_leg_fan`. */
export const branchOf = (
  d: FieldDefinition | undefined,
  fieldExpands: (def?: FieldDefinition) => boolean,
): string | null => (
  d && fieldExpands(d) && d.join_key
    ? (d.expanding_branch || EXPANDING_BRANCH_FALLBACK[d.join_key] || d.join_key)
    : null
);

/** Keep every non-expanding field plus the FIRST expanding branch in selection order;
 *  drop the rest. Mirrors the backend's resolve_requested_fields, which does the same
 *  thing server-side — this exists so the user is told before the search runs. */
export function enforceSingleExpandingBranch(
  fields: string[],
  defByName: Map<string, FieldDefinition>,
  fieldExpands: (def?: FieldDefinition) => boolean,
): { kept: string[]; dropped: string[] } {
  let chosen: string | null = null;
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const fn of fields) {
    const branch = branchOf(defByName.get(fn), fieldExpands);
    if (branch === null) { kept.push(fn); continue; }
    if (chosen === null) chosen = branch;
    if (branch === chosen) kept.push(fn); else dropped.push(fn);
  }
  return { kept, dropped };
}
